import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { db } from '../../db';

export const loadInitialSettings = createAsyncThunk(
    'chartSettings/loadInitialSettings',
    async (_, { dispatch }) => {
        try {
            const settings = await db.settings.get('measurement');
            dispatch(setUnitSystem(settings?.unitSystem || 'imperial'))
            dispatch(setShowUserLocation(settings?.showUserLocation || false))
            return settings || { unitSystem: 'imperial', showUserLocation: false };
        } catch (error) {
            console.error('Error loading settings:', error);
            return { unitSystem: 'imperial', showUserLocation: false };
        }
    }
);

export const cacheChartSettings = createAsyncThunk(
    'chartSettings/cacheChartSettings',
    async ({ routeId, chartSettings }, { getState }) => {
        try {
            // Create route-specific chart settings by excluding app-wide settings like showUserLocation
            const routeSpecificChartSettings = { ...chartSettings };
            delete routeSpecificChartSettings.showUserLocation;
            delete routeSpecificChartSettings.unitSystem;
            delete routeSpecificChartSettings.elevUnit;
            delete routeSpecificChartSettings.elevAbbr;
            delete routeSpecificChartSettings.distUnit;
            delete routeSpecificChartSettings.distAbbr;
            delete routeSpecificChartSettings.distMultiplier;
            delete routeSpecificChartSettings.elevMultiplier;

            // Update the route record with the new chart settings
            await db.routes.update(parseInt(routeId), {
                chartSettings: routeSpecificChartSettings
            });
            return { routeId, chartSettings: routeSpecificChartSettings };
        } catch (error) {
            console.error('Error caching chart settings:', error);
            throw error;
        }
    }
);

export const updateSettings = createAsyncThunk(
    'chartSettings/updateSettings',
    async ({ unitSystem, showUserLocation }) => {
        await db.settings.put({
            unitSystem,
            showUserLocation,
            id: 'measurement'
        });
        return { unitSystem, showUserLocation };
    }
);

export const chartSettingsSlice = createSlice({
    name: 'chartSettings',
    initialState: {
        settingsLoaded: false, // Flag to track if settings have been loaded from DB
        chartSettings: {
            unitSystem: 'imperial',
            showUserLocation: false,
            elevUnit: "meter",
            elevAbbr: "m",
            distUnit: "kilometer",
            distAbbr: "km",
            distMultiplier: 0.001,
            elevMultiplier: 1,
            distance: 1000,
            uniformElevationScale: true,
            chartHeight: 2,
            poiFontSize: 0.9,
            showGrid: true,
            elevationPointsDensity: 2,
            sideBySideLayout: false,
            offRouteThreshold: 0.25, // Distance in miles
            showPoiDescriptions: false, // New setting
            showDistanceToNextPoi: false,
            hideOffRoutePoi: false,
        }
    },
    reducers: {
        setSettingsLoaded: (state, action) => {
            state.settingsLoaded = action.payload;
        },
        setUnitSystem(state, action) {
            state.chartSettings.unitSystem = action.payload;
            if (action.payload === "imperial") {
                state.chartSettings.elevUnit = "feet"
                state.chartSettings.elevAbbr = "ft"
                state.chartSettings.distUnit = "mile"
                state.chartSettings.distAbbr = "mi"
                state.chartSettings.distMultiplier = 0.000621371; //meters to miles
                state.chartSettings.elevMultiplier = 3.28084; //meters to feet
            } else {
                state.chartSettings.elevUnit = "meter"
                state.chartSettings.elevAbbr = "m"
                state.chartSettings.distUnit = "kilometer"
                state.chartSettings.distAbbr = "km"
                state.chartSettings.distMultiplier = 0.001; //meters to km
                state.chartSettings.elevMultiplier = 1; //meters to meters
            }
        },
        setDistance(state, action) {
            state.chartSettings.distance = action.payload;
        },
        setUniformElevationScale(state, action) {
            state.chartSettings.uniformElevationScale = action.payload;
        },
        setChartHeight(state, action) {
            state.chartSettings.chartHeight = action.payload;
        },
        setPoiFontSize(state, action) {
            state.chartSettings.poiFontSize = action.payload;
        },
        setShowGrid(state, action) {
            state.chartSettings.showGrid = action.payload;
        },
        setElevationPointsDensity(state, action) {
            state.chartSettings.elevationPointsDensity = action.payload;
        },
        setSideBySideLayout(state, action) {
            state.chartSettings.sideBySideLayout = action.payload;
        },
        setOffRouteThreshold(state, action) {
            state.chartSettings.offRouteThreshold = action.payload;
        },
        setShowPoiDescriptions(state, action) {
            state.chartSettings.showPoiDescriptions = action.payload;
        },
        setShowDistanceToNextPoi(state, action) {
            state.chartSettings.showDistanceToNextPoi = action.payload;
        },
        setHideOffRoutePoi(state, action) {
            state.chartSettings.hideOffRoutePoi = action.payload;
        },
        setChartSettings(state, action) {
            state.chartSettings = action.payload;
        },
        clearChartSettings(state, action) {
            // reset the distance to empty string so optimal chunk size will be calculated on next route load
            state.chartSettings = { ...state.chartSettings, distance: '' }
        },
        setShowUserLocation(state, action) {
            state.chartSettings.showUserLocation = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadInitialSettings.fulfilled, (state) => {
                state.settingsLoaded = true;
            })
            .addCase(updateSettings.fulfilled, (state, action) => {
                const { unitSystem, showUserLocation } = action.payload;
                state.chartSettings.unitSystem = unitSystem;
                state.chartSettings.showUserLocation = showUserLocation;
            });
    },
})

// Action creators are generated for each case reducer function
export const {
    setSettingsLoaded,
    setUnitSystem,
    setDistance,
    setUniformElevationScale,
    setChartHeight,
    setPoiFontSize,
    setShowGrid,
    setElevationPointsDensity,
    setSideBySideLayout,
    setOffRouteThreshold,
    setShowPoiDescriptions,
    setShowDistanceToNextPoi,
    setHideOffRoutePoi,
    setChartSettings,
    clearChartSettings,
    setShowUserLocation,
} = chartSettingsSlice.actions

export default chartSettingsSlice.reducer
